import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, X } from "lucide-react";
import { track } from "../../lib/analytics";

/**
 * First-open hint that nudges users to try the wake-word voice
 * commands. Apple-safe (no purchase / pricing / external links).
 *
 * Anchored just below the top bar, pointing visually at the mic
 * toggle button (top-right). Auto-dismisses after 8s. Persists
 * dismissal in localStorage so it only ever shows once per user.
 *
 * Hidden when `voiceSupported === false` — speech recognition isn't
 * available in this browser, so the suggestion would be misleading.
 */
const STORAGE_KEY = "trubeaute.trouve.wake_word_hint_shown.v1";
const SHOW_AFTER_MS = 1200; // small delay so the mirror has time to settle
const AUTO_DISMISS_MS = 8000;

const MirrorWakeWordHint = ({ voiceSupported, voiceEnabled }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!voiceSupported) return;
    // Don't pop the hint if the user has already opted into voice —
    // they don't need the nudge.
    if (voiceEnabled) return;
    try {
      if (window.localStorage?.getItem(STORAGE_KEY)) return;
    } catch {
      /* private browsing — fall through and show */
    }
    const t = setTimeout(() => {
      setVisible(true);
      track("mirror_wake_hint_shown");
      try {
        window.localStorage?.setItem(STORAGE_KEY, "1");
      } catch {
        /* no-op */
      }
    }, SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, [voiceSupported, voiceEnabled]);

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => setVisible(false), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [visible]);

  // If the user enables voice mid-hint, dismiss immediately — the
  // nudge has done its job.
  useEffect(() => {
    if (voiceEnabled && visible) {
      setVisible(false);
      track("mirror_wake_hint_followed");
    }
  }, [voiceEnabled, visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.32, ease: "easeOut" }}
          // Sit just under the top bar, anchored to the right so the
          // little tail arrow visually points up at the mic toggle.
          className="absolute right-5 z-30 max-w-[280px] sm:max-w-[320px]"
          style={{
            top: "calc(max(env(safe-area-inset-top), 20px) + 64px)",
          }}
          data-testid="mirror-wake-hint"
          role="status"
          aria-live="polite"
        >
          {/* Tail pointing to the mic button — keeps the hint visually
              tethered to its trigger */}
          <div className="absolute -top-1.5 right-7 h-3 w-3 rotate-45 bg-[#1a1416]/95 border-l border-t border-[#D5A8A4]/40" />
          <div className="relative bg-[#1a1416]/95 backdrop-blur-xl border border-[#D5A8A4]/40 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.45)] p-3.5 pr-9">
            <button
              type="button"
              onClick={() => {
                setVisible(false);
                track("mirror_wake_hint_dismissed");
              }}
              className="absolute top-2 right-2 text-white/40 hover:text-white/80 transition-colors"
              aria-label="Dismiss hint"
              data-testid="mirror-wake-hint-dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <div className="flex items-start gap-2.5">
              <div className="shrink-0 h-7 w-7 rounded-full bg-[#D5A8A4]/25 flex items-center justify-center mt-0.5">
                <Mic className="h-3.5 w-3.5 text-[#FFE6D7]" />
              </div>
              <div className="text-white text-[12.5px] leading-snug">
                <div className="font-medium text-[#FFE6D7] mb-0.5">
                  Try hands-free
                </div>
                <div className="text-white/80">
                  Tap the mic, then say{" "}
                  <span className="italic text-[#FFE6D7]">
                    "Hey Trouvé, next"
                  </span>
                  .
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MirrorWakeWordHint;
